/**
 * How the Status page survives a reload (ORDR-166).
 *
 * Before this, the order id lived in React state only: a diner who reloaded the
 * page they were told to keep open lost their order and landed on the menu.
 *
 * - THE ORDER ID IS IN THE ADDRESS, as `?order=<commerce order id>`, written
 *   with `replaceState` once checkout produced the order. It is a public key
 *   already (the status and order reads are AllowAny on it), and in the address
 *   it survives a reload, a crash and a phone that drops the tab.
 * - WHEN EACH STAGE WAS FIRST SEEN is a per-tab nicety in sessionStorage. Lost,
 *   the steps simply show no time; nothing else depends on it.
 *
 * Storage can be absent or throw, so every access is guarded.
 */

import type { Stage } from "./statusApi.js";

export const ORDER_PARAM = "order";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SEEN_PREFIX = "paio-ordering-ui:order-seen:";

/** The `?order=` id, or `null` when absent or not a uuid. */
export function orderFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(ORDER_PARAM);
  return value !== null && UUID.test(value) ? value.toLowerCase() : null;
}

/** Puts the order id in the address, keeping everything else in it. */
export function rememberOrder(orderId: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set(ORDER_PARAM, orderId);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export type SeenTimes = Partial<Record<Stage, number>>;

export interface SeenStore {
  read(orderId: string): SeenTimes;
  write(orderId: string, times: SeenTimes): void;
}

function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function createSeenStore(backing: () => Storage | null = sessionStore): SeenStore {
  return {
    read(orderId) {
      try {
        const raw = backing()?.getItem(SEEN_PREFIX + orderId) ?? null;
        const parsed: unknown = raw === null ? null : JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? (parsed as SeenTimes) : {};
      } catch {
        return {};
      }
    },
    write(orderId, times) {
      try {
        backing()?.setItem(SEEN_PREFIX + orderId, JSON.stringify(times));
      } catch {
        // The times stay in memory for this mount.
      }
    },
  };
}
